<template>
	<layout-admin>

		<div class="py-6 sm:px-6 flex items-center justify-between">
			<h1 class="text-3xl font-semibold text-gray-900">{{ __("Contract") }}</h1>
			<ui-tooltip
				v-if="canAdmin && contract.date.type === 'STAGGERED'"
				text="Muss im SVM-Modul storniert werden"
			>
				<template v-slot:label>
					<ui-button
						label="Kontrakt stornieren"
						class="bg-red-500 hover:bg-red-600"
						type="primary"
						:disabled="true"
					/>
				</template>
			</ui-tooltip>
			<ui-button
				v-else-if="canAdmin"
				v-on:click="openCancelModal()"
				label="Kontrakt stornieren"
				class="bg-red-500 hover:bg-red-600"
				type="primary"
				:disabled="!!contract.canceled_at"
			/>
		</div>

		<div class="sm:px-6">
		<div
			v-if="contract.canceled_at"
			class="flex items-center gap-3 my-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700"
		>
			<ui-icon name="alert-triangle" class="h-5 w-5 flex-shrink-0" stroke-width="2" />
			<span class="font-medium">Dieser Kontrakt wurde storniert.</span>
		</div>
		<div class="space-y-2 my-2">
			<div class="grid grid-cols-3">
				<h4 class="text-sm text-gray-900">{{ __("Seller") }}</h4>
				<p class="col-span-2 text-sm">
					{{ contract.seller.company_name }}<br />
					{{ contract.seller.address.street }}, {{ contract.seller.address.zip }} {{ contract.seller.address.city }}<br />
					{{ contract.seller.email }}
				</p>
			</div>
			<div class="grid grid-cols-3 border-t border-gray-200 pt-2">
				<h4 class="text-sm text-gray-900">{{ __("Buyer") }}</h4>
				<p class="col-span-2 text-sm">
					{{ contract.buyer.company_name }}<br />
					{{ contract.buyer.address.street }}, {{ contract.buyer.address.zip }} {{ contract.buyer.address.city }}<br />
					{{ contract.buyer.email }}
				</p>
			</div>
		</div>

		<hr class="my-2" />
		<tradable-terms-summary
			:offer="contract"
			v-bind:extended="true"
			:large-view="true"
		/>
		<hr class="my-2" />
		<div v-if="!showUploadForm" class="flex flex-wrap gap-2">
			<ui-button
				v-on:click="
					getPdf(
						$route('admin.contracts_pdf', { id: contract.id, role: 'seller' })
					)
				"
				label="Confirmation PDF / Seller"
			/>
			<ui-button
				v-on:click="
					getPdf(
						$route('admin.contracts_pdf', { id: contract.id, role: 'buyer' })
					)
				"
				label="Confirmation PDF / Buyer"
			/>
			<ui-button
				v-on:click="showUploadForm = true"
				label="Upload Changed Contract PDF"
				type="secondary"
				v-if="canUpload && !showUploadForm"
			/>
			<ui-tooltip
				v-if="canAdmin && contract.date.type === 'STAGGERED'"
				text="Muss im SVM-Modul bearbeitet werden"
			>
				<template v-slot:label>
					<ui-button
						label="Kontrakt bearbeiten"
						type="secondary"
						:disabled="true"
					/>
				</template>
			</ui-tooltip>
			<ui-tooltip
				v-else-if="canAdmin && contract.canceled_at"
				text="Stornierte Kontrakte können nicht bearbeitet werden"
			>
				<template v-slot:label>
					<ui-button
						label="Kontrakt bearbeiten"
						type="secondary"
						:disabled="true"
					/>
				</template>
			</ui-tooltip>
			<ui-button
				v-else-if="canAdmin"
				v-on:click="openEditModal()"
				label="Kontrakt bearbeiten"
				type="secondary"
			/>
		</div>

		<div v-if="showUploadForm">
			<h2 class="text-2xl font-semibold text-gray-900">
				{{ __("Upload Changed Contract PDF") }}
			</h2>
			<form @submit.prevent="submit" class="space-y-6">
				<div>
					<label class="block font-medium">{{
						__("Confirmation PDF / Seller")
					}}</label>
					<input
						type="file"
						@input="form.seller_pdf = $event.target.files[0]"
					/>
					<ui-validation-message :form="form" field="seller_pdf" />
				</div>

				<div>
					<label class="block font-medium">{{
						__("Confirmation PDF / Buyer")
					}}</label>
					<input type="file" @input="form.buyer_pdf = $event.target.files[0]" />
					<ui-validation-message :form="form" field="buyer_pdf" />
				</div>

				<progress
					v-if="form.progress"
					:value="form.progress.percentage"
					max="100"
				>
					{{ form.progress.percentage }}%
				</progress>
				<div class="pb-4" v-else>
					<ui-button v-on:click="submit()" label="Upload" />
					<ui-button
						v-on:click="showUploadForm = false"
						label="Cancel"
						type="secondary"
						class="pl-4"
					/>
				</div>
			</form>
		</div>
		</div>
	</layout-admin>
</template>

<script>
export default {
	props: {
		contract: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			canUpload: this.$inertia.page.props.acl.upload_contract_pdf,
			canAdmin: this.$inertia.page.props.acl['contracts.admin'],
			showUploadForm: false,
			form: Vue.observable(
				this.$inertia.form({
					buyer_pdf: null,
					seller_pdf: null,
				})
			),
		};
	},
	computed: {
		contractConfirmUrl: function () {
			return (
				window.baseUrl + "/contract/" + this.contract.id + "/confirmation_pdf"
			);
		},
	},
	methods: {
		getPdf(url) {
			window.location = url;
		},
		openEditModal() {
			this.$bus.$emit("modal:open", {
				view: {
					view: "views/Admin/ContractForm",
					data: {
						id: this.contract.id,
						qty__mt: this.contract.qty.mt,
					},
				},
				size: "md",
			});
		},
		openCancelModal() {
			this.$bus.$emit("modal:open", {
				view: {
					view: "views/Admin/ContractCancel",
					data: { id: this.contract.id },
				},
				size: "sm",
			});
		},
		submit() {
			let that = this;
			this.form.post(
				this.$route("admin.contracts_upload", { id: this.contract.id }),
				{
					onSuccess() {
						that.showUploadForm = false;
						alert("Contracts updated!");
					},
				}
			);
		},
	},
};
</script>
